/**
 * Sets, job profiles and tags (issue #36, decisions 3 and 4).
 *
 * ORDER BANDS ARE DISJOINT AND THAT IS LOAD-BEARING. `order` is global, not set-local, so
 * two sets composed onto one person interleave strictly by it. Overlapping bands would
 * slot one set's rows into the middle of another's run. `tests/catalog-seed.test.ts`
 * asserts they stay disjoint.
 */
export const requirementSetSeeds = [
    /*
     * September 13, 2026 (Julio, #147), PARTLY REVERSED 2026-09-30 (Paul, #372).
     *
     * The original claim was that the signed HR 4.13A.3 Orientation Checklist covers confidentiality
     * (item 7), personnel policies and the handbook (item 9) and the Sexual Harassment Act (item 37),
     * so those three rows need no separate item on any checklist.
     *
     * CONFIDENTIALITY IS BACK ON, with its `employment` gate and `waivable: true` — the other two
     * things #147 took off it. Paul: "HIPAA. Keep it as its own form. The Orientation Checklist
     * doesn't cover any of the portal steps, so please strike the Sept 13 note … and switch 'Signed
     * confidentiality agreement' back on for the roles." It is a portal form in its own right, and it
     * is the HIPAA form on the target list — the two are one item in one sentence of his.
     *
     * The handbook (item 9) and harassment (item 37) rows are STILL OFF, and the reasoning behind
     * their removal is now IN DOUBT rather than disproved: Paul's sentence is about portal steps, and
     * neither row is on the target portal-form list, so it does not clearly reach them. He asked only
     * for confidentiality. #372 carries the question; re-enabling a gating compliance row nobody
     * asked for is not a tidy-up.
     *
     * Education is verified only for roles without a license, certification or qualification row
     * ("education or license", Policy Revision Tracker item 12), so `credential-verification` moved
     * from this set to those profiles. The rows keep their keys and any retained evidence.
     */
    {
        key: 'set-core',
        label: 'Core — every employee and contractor',
        orderBand: [
            0,
            199
        ],
        members: [
            'initial-clearance-scope-review',
            'information-truthfulness-statement',
            'compliance-coi-orientation',
            'annual-compliance-education',
            'criminal-sworn-disclosure',
            'tb-baseline-risk-assessment',
            'hepb-info-sheet-issued',
            'hepb-offer-and-response',
            'respirator-medical-clearance',
            'respirator-fit-test',
            'respirator-training',
            'initial-competency-before-independent-care',
            'internal-job-posting',
            'employment-application',
            'reference-checks',
            'work-history',
            'i9-form',
            'criminal-background-check',
            'oig-leie-exclusion-check',
            'nsopw-sex-offender-check',
            'conviction-review-determination',
            'job-description-signed',
            /*
             * BACK ON, AND BACK WHERE IT WAS, 2026-09-30 (Paul, #372).
             *
             * Restored to its pre-#147 index rather than appended. A first pass appended it, arguing that
             * a member's `order` is `orderBand[0] + its index`, so an insertion renumbers every row below.
             * That is true and harmless — `order` is computed at resolve time and never persisted, the
             * sort tiebreaks on `requirementKey`, and renumbering preserves relative order. What appending
             * DID do was sort an at-hire signature after the separation and return-to-work rows, which a
             * review of #397 measured at position 57 of 90 for an RN.
             */
            'confidentiality-agreement',
            /*
             * PV1 (#372 part 2, Paul 2026-09-29). Beside the confidentiality agreement above it, because
             * they are two pages of the SAME signed packet — Paul, asked whether both are in it: "yes
             * both part of the onboarding/employee packet/application". Placed rather than appended, for
             * the reason the note above records.
             *
             * `set-core`, so every role. The precedent next door points the other way —
             * `mandated-reporter-acknowledgment` is `set-clinical` — and on the title alone ("conduct in
             * patients' homes") that would fit. What the agency actually collects decides it: every
             * employee signs this packet today.
             */
            'patient-visit-rules-acknowledgment',
            'withholding-statements',
            'wage-salary-record',
            'benefits-record',
            'benefits-discussion-at-hire',
            'emergency-contact',
            'change-of-status-forms',
            'attendance-record',
            'employment-action-records',
            'negative-outcomes-record',
            'separation-record',
            'personnel-record-retention',
            'personnel-info-release-authorization',
            'employee-file-review-supervised',
            'health-file-segregation',
            'orientation-completion',
            'orientation-job-specific-and-equipment-training',
            'inservice-education-record',
            'bloodborne-pathogens-training',
            'tb-education-training',
            'hazmat-right-to-know-training',
            'emergency-operations-plan-training',
            'infection-control-training',
            'workplace-violence-prevention-training',
            'safety-roles-responsibilities-training',
            'staff-personal-safety-education',
            'performance-appraisal',
            'performance-appraisal-role-specific-instrument',
            'performance-appraisal-dual-signature',
            'annual-job-duties-evaluation',
            'informal-ongoing-performance-review',
            'disciplinary-probation-period',
            'occupational-exposure-incident-record',
            'fitness-for-duty-return-to-work',
            'absence-medical-documentation',
        ]
    },
    {
        key: 'set-clinical',
        label: 'Clinical — licensure, competency and clinical performance',
        orderBand: [
            200,
            299
        ],
        members: [
            'license-copy-at-hire',
            'license-attestation-letter',
            'psv-at-hire',
            'psv-at-renewal',
            'psv-verification-record-retained',
            'supervisor-credential-notification',
            'license-lapse-work-stoppage',
            'specialty-certification',
            'new-hire-close-supervision',
            'initial-competency-before-independent-care',
            'ongoing-competency-triennial',
            'competency-on-new-procedure-or-equipment',
            'ceu-validation-clinical-staff',
            'unsatisfactory-performance-investigation-remediation',
            'visit-signature-policy-attestation',
            'visit-verification-re-education-record',
            'mandated-reporter-acknowledgment'
        ]
    },
    {
        key: 'set-health',
        label: 'Employee health screening (segregated records)',
        orderBand: [
            300,
            349
        ],
        members: [
            'tb-baseline-risk-assessment',
            'tb-symptom-evaluation',
            'tb-test-baseline',
            'tb-periodic-retest',
            'tb-positive-followup',
            'hepb-info-sheet-issued',
            'hepb-offer-and-response',
            'influenza-vaccination-status'
        ]
    },
    {
        key: 'set-cpr',
        label: 'CPR — direct-care profiles only (decision 4)',
        orderBand: [
            350,
            379
        ],
        members: ['cpr-certification', 'cpr-online-inperson-verification']
    },
    {
        key: 'set-driving',
        label: 'Driving — attaches through the driver tag',
        orderBand: [
            400,
            449
        ],
        members: ['drivers-license', 'motor-vehicle-record-check', 'personal-vehicle-insurance']
    },
];
/**
 * The 14 MVP profiles (decision 3). Social Work Assistant and Students are deliberately
 * absent — see `droppedFromCatalog` in index.ts.
 *
 * `directCare` is how decision 4 attaches CPR: the CPR set is included by direct-care
 * profiles, so "who needs CPR" is answered by set membership rather than by an `if` on a
 * requirement key anywhere in the app.
 */
export const jobProfileSeeds = [
    {
        key: 'rn',
        label: 'Registered Nurse (RN)',
        directCare: true,
        includes: ['set-core', 'set-clinical', 'set-health', 'set-cpr'],
        adds: [
            'rn-license',
            'competency-evaluator-qualification',
            'hha-trainer-rn-experience-qualification',
            'hha-supervisory-visit-14-day-skilled',
            'hha-supervisory-visit-60-day-nonskilled',
            'hha-semiannual-direct-observation-nonskilled',
            'hha-concern-triggered-supervisory-visit',
            'hha-virtual-supervision-cap',
            'student-supervision-by-agency-staff',
            'evv-policy-acknowledgment'
        ]
    },
    {
        key: 'lpn-lvn',
        label: 'Licensed Practical / Vocational Nurse (LPN/LVN)',
        directCare: true,
        includes: ['set-core', 'set-clinical', 'set-health', 'set-cpr'],
        adds: ['lpn-license', 'evv-policy-acknowledgment']
    },
    {
        key: 'pt',
        label: 'Physical Therapist (PT)',
        directCare: true,
        includes: ['set-core', 'set-clinical', 'set-health', 'set-cpr'],
        adds: [
            'pt-license',
            'pta-ota-supervisory-visit',
            'competency-evaluator-qualification',
            'hha-supervisory-visit-14-day-skilled',
            'hha-concern-triggered-supervisory-visit',
            'student-supervision-by-agency-staff'
        ]
    },
    {
        key: 'pta',
        label: 'Physical Therapist Assistant (PTA)',
        directCare: true,
        includes: ['set-core', 'set-clinical', 'set-health', 'set-cpr'],
        adds: [
            'pta-license',
            'pta-ota-supervising-therapist-assignment',
            'pta-ota-supervisory-visit'
        ]
    },
    {
        key: 'ot',
        label: 'Occupational Therapist (OT)',
        directCare: true,
        includes: ['set-core', 'set-clinical', 'set-health', 'set-cpr'],
        adds: [
            'ot-license',
            'pta-ota-supervisory-visit',
            'competency-evaluator-qualification',
            'hha-supervisory-visit-14-day-skilled',
            'hha-concern-triggered-supervisory-visit',
            'student-supervision-by-agency-staff'
        ]
    },
    {
        key: 'cota',
        label: 'Certified Occupational Therapy Assistant (COTA)',
        directCare: true,
        includes: ['set-core', 'set-clinical', 'set-health', 'set-cpr'],
        adds: [
            'cota-certification',
            'pta-ota-supervising-therapist-assignment',
            'pta-ota-supervisory-visit'
        ]
    },
    {
        key: 'slp',
        label: 'Speech-Language Pathologist (SLP)',
        directCare: true,
        includes: ['set-core', 'set-clinical', 'set-health', 'set-cpr'],
        adds: [
            'slp-license',
            'competency-evaluator-qualification',
            'hha-supervisory-visit-14-day-skilled',
            'hha-concern-triggered-supervisory-visit',
            'student-supervision-by-agency-staff'
        ]
    },
    {
        key: 'msw',
        label: 'Medical Social Worker (MSW)',
        directCare: true,
        includes: ['set-core', 'set-clinical', 'set-health', 'set-cpr'],
        adds: [
            'msw-degree',
            'msw-license',
            'competency-evaluator-qualification',
            'student-supervision-by-agency-staff'
        ]
    },
    {
        key: 'hha',
        label: 'Home Health Aide (HHA)',
        directCare: true,
        includes: ['set-core', 'set-clinical', 'set-health', 'set-cpr'],
        adds: [
            'hha-qualification-pathway',
            'hha-state-registry-good-standing',
            'hha-24-month-lapse-rule',
            'hha-initial-onsite-competency',
            'hha-annual-onsite-competency',
            'hha-inservice-12-hours-rn-supervised',
            'hha-unsatisfactory-task-remediation',
            'hha-deficiency-retraining-and-competency-eval',
            'hha-supervisory-visit-14-day-skilled',
            'hha-supervisory-visit-60-day-nonskilled',
            'hha-semiannual-direct-observation-nonskilled',
            'hha-concern-triggered-supervisory-visit',
            'hha-virtual-supervision-cap',
            'performance-appraisal-hha-annual',
            'evv-policy-acknowledgment'
        ],
        removes: [
            'psv-at-hire',
            'psv-at-renewal',
            'specialty-certification',
            'ongoing-competency-triennial',
            'initial-competency-before-independent-care',
            'ceu-validation-clinical-staff'
        ]
    },
    {
        key: 'administrator',
        label: 'Administrator',
        directCare: false,
        includes: ['set-core'],
        adds: [
            'credential-verification',
            'administrator-qualification',
            'personnel-file-access-control',
            'wage-scale-annual-review',
            'retention-program-elements',
            'annual-tb-risk-assessment-agency',
            'influenza-program-annual-evaluation',
            'agency-vehicle-insurance'
        ]
    },
    {
        key: 'dcs',
        label: 'Director of Clinical Services (DCS)',
        directCare: true,
        includes: ['set-core', 'set-clinical', 'set-health', 'set-cpr'],
        adds: [
            'dcs-qualification',
            'rn-license',
            'personnel-file-access-control',
            'competency-evaluator-qualification',
            'retention-program-elements',
            'annual-tb-risk-assessment-agency',
            'influenza-program-annual-evaluation',
            'student-supervision-by-agency-staff'
        ]
    },
    {
        key: 'clinical-manager',
        label: 'Clinical Manager',
        directCare: true,
        includes: ['set-core', 'set-clinical', 'set-health', 'set-cpr'],
        adds: [
            'clinical-manager-qualification',
            'personnel-file-access-control',
            'competency-evaluator-qualification'
        ]
    },
    {
        key: 'qapi-coordinator',
        label: 'QAPI Coordinator',
        directCare: false,
        includes: ['set-core'],
        adds: [
            'credential-verification',
            'qapi-coordinator-qualification',
            'retention-program-elements',
            'annual-tb-risk-assessment-agency',
            'influenza-program-annual-evaluation'
        ]
    },
    {
        key: 'office',
        label: 'Office / administrative staff',
        directCare: false,
        includes: ['set-core'],
        adds: ['credential-verification']
    },
];
/**
 * Contractor and Driver are TAGS, not profiles (decision 3): a contracted nurse is still a
 * nurse, and nobody is duplicated as an "RN (driving)".
 *
 * The contractor tag adds only contractor-SPECIFIC rows. It deliberately does not restate
 * the baseline set — the person already has that through their profile.
 */
export const tagProfileSeeds = [
    {
        tag: 'contractor',
        label: 'Contract staff',
        includes: [],
        adds: [
            'contract-written-agreement',
            'contract-staff-file-accessibility',
            'contract-staff-licensed-before-service',
            'conflict-of-interest',
            'contract-hha-supervision-and-competency'
        ]
    },
    {
        tag: 'driver',
        label: 'Drives on Agency business',
        includes: ['set-driving'],
        adds: []
    },
];
//# sourceMappingURL=profiles.seed.js.map